import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Database, RefreshCw, Send, CheckCircle2, AlertCircle, Download, Upload, ShieldAlert } from "lucide-react";

function formatBytes(n) {
    if (!n || n <= 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function formatTimestamp(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (isNaN(d)) return iso;
        return d.toLocaleString("en-NG", {
            dateStyle: "medium",
            timeStyle: "short",
        });
    } catch {
        return iso;
    }
}

function relativeTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const diffMs = Date.now() - d.getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return "just now";
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
    const mo = Math.floor(day / 30);
    return `${mo} month${mo === 1 ? "" : "s"} ago`;
}

export default function Backups() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [restoreOpen, setRestoreOpen] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/notifications/backup/history?limit=100");
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load backup history");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const onRunBackup = async () => {
        if (!window.confirm("Generate a fresh Excel snapshot of the HR database and email it to all admins now?")) return;
        setRunning(true);
        try {
            const { data } = await api.post("/notifications/backup/run");
            toast.success(`Backup emailed · ${data.rows} rows · ${formatBytes(data.size_bytes)} · to ${data.recipients?.length || 0} admin${data.recipients?.length === 1 ? "" : "s"}`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Backup failed");
            load();
        } finally {
            setRunning(false);
        }
    };

    const onDownloadNow = async () => {
        setDownloading(true);
        try {
            const res = await api.get("/notifications/backup/download", { responseType: "blob" });
            // Derive filename from Content-Disposition or fall back to default
            const cd = res.headers?.["content-disposition"] || "";
            const match = cd.match(/filename="?([^"]+)"?/);
            const filename = match ? match[1] : `hrm_backup_${new Date().toISOString().slice(0, 10)}.xlsx`;
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Downloaded ${filename}`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Download failed");
        } finally {
            setDownloading(false);
        }
    };

    const stats = useMemo(() => {
        if (!items.length) return null;
        const delivered = items.filter((i) => i.delivered).length;
        const lastOk = items.find((i) => i.delivered);
        const totalSize = items.reduce((s, i) => s + (i.size_bytes || 0), 0);
        return {
            total: items.length,
            delivered,
            lastOkAt: lastOk?.created_at || "",
            totalSize,
        };
    }, [items]);

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="backups-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Audit & retention</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.02]">
                        Backups
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-2xl">
                        Every weekly backup that has been generated and emailed to admins. Auto-runs every <strong className="text-ink">Sunday at 02:00 (Africa/Lagos)</strong>. Use this as your offsite-recovery audit trail.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={load}
                        disabled={loading}
                        className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink hover:text-white transition-colors disabled:opacity-60"
                        data-testid="backups-refresh-btn"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </button>
                    <button
                        onClick={onDownloadNow}
                        disabled={downloading}
                        className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink hover:text-white transition-colors disabled:opacity-60"
                        data-testid="backups-download-btn"
                        title="Generate a fresh snapshot and download the .xlsx directly (doesn't email)"
                    >
                        <Download className="w-4 h-4" /> {downloading ? "Generating…" : "Download .xlsx"}
                    </button>
                    <button
                        onClick={() => setRestoreOpen(true)}
                        className="inline-flex items-center gap-2 border border-amber-500 text-amber-700 px-4 py-2.5 text-sm hover:bg-amber-500 hover:text-white transition-colors"
                        data-testid="backups-restore-btn"
                        title="Restore data from a previously-generated backup .xlsx"
                    >
                        <Upload className="w-4 h-4" /> Restore…
                    </button>
                    <button
                        onClick={onRunBackup}
                        disabled={running}
                        className="inline-flex items-center gap-2 bg-klein text-white px-5 py-2.5 text-sm hover:bg-klein/90 transition-colors disabled:opacity-60"
                        data-testid="backups-run-btn"
                    >
                        <Send className="w-4 h-4" /> {running ? "Generating…" : "Run new backup"}
                    </button>
                </div>
            </div>

            {/* Stats strip */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10 border border-ink/10" data-testid="backups-stats">
                <Stat label="Total backups" value={loading ? "—" : stats?.total ?? 0} />
                <Stat label="Delivered" value={loading ? "—" : stats?.delivered ?? 0} accent />
                <Stat label="Last successful" value={loading ? "—" : (stats?.lastOkAt ? relativeTime(stats.lastOkAt) : "—")} />
                <Stat label="Cumulative size" value={loading ? "—" : formatBytes(stats?.totalSize)} />
            </div>

            {/* Table */}
            <div className="mt-10 bg-white border border-ink/10" data-testid="backups-table">
                <div className="px-6 py-4 border-b border-ink/10 flex items-center justify-between">
                    <div className="overline">History</div>
                    <div className="text-xs text-ink/50">{items.length} record{items.length === 1 ? "" : "s"}</div>
                </div>
                {loading ? (
                    <div className="p-10 text-sm text-ink/40">Loading backup history…</div>
                ) : items.length === 0 ? (
                    <div className="p-10 text-sm text-ink/50" data-testid="backups-empty">
                        No backups have run yet. Click <strong>Run new backup</strong> above to create the first snapshot.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-ink/50 border-b border-ink/10">
                                    <th className="px-6 py-3 font-medium">When</th>
                                    <th className="px-4 py-3 font-medium">Status</th>
                                    <th className="px-4 py-3 font-medium text-right">Rows</th>
                                    <th className="px-4 py-3 font-medium text-right">Size</th>
                                    <th className="px-4 py-3 font-medium">Recipients</th>
                                    <th className="px-4 py-3 font-medium">Resend ID</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it) => {
                                    const isOpen = expandedId === it.id;
                                    return (
                                        <BackupRow
                                            key={it.id}
                                            item={it}
                                            isOpen={isOpen}
                                            onToggle={() => setExpandedId(isOpen ? null : it.id)}
                                        />
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <p className="mt-6 text-xs text-ink/50">
                Tip: backups exclude password hashes. Each entry above is a row in <code>notifications_log</code> (type=backup).
            </p>

            {restoreOpen && <RestoreDialog onClose={() => setRestoreOpen(false)} onDone={load} />}
        </div>
    );
}

function RestoreDialog({ onClose, onDone }) {
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [mode, setMode] = useState("upsert");
    const [includeUsers, setIncludeUsers] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);

    const onSubmit = async () => {
        if (!file) {
            toast.error("Please choose a backup .xlsx file first");
            return;
        }
        if (mode === "replace") {
            const ok = window.confirm(
                "⚠️ REPLACE mode will WIPE every collection (except your own admin user) and overwrite with this file's contents.\n\nThis is DESTRUCTIVE and CANNOT BE UNDONE.\n\nAre you absolutely sure?"
            );
            if (!ok) return;
            const ok2 = window.prompt('Type RESTORE to confirm (case-sensitive):');
            if (ok2 !== "RESTORE") {
                toast.error("Confirmation phrase did not match — restore cancelled");
                return;
            }
        }
        setRunning(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const params = new URLSearchParams({ mode, include_users: includeUsers ? "true" : "false" });
            const { data } = await api.post(`/notifications/backup/restore?${params.toString()}`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            toast.success(`Restore complete · ${data.total_inserted} inserted, ${data.total_updated} updated`);
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Restore failed");
        } finally {
            setRunning(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start justify-center pt-16 px-4 overflow-y-auto"
            onClick={() => !running && onClose()}
            data-testid="restore-dialog"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white border border-ink/10 w-full max-w-xl mb-16"
            >
                <div className="px-6 py-5 border-b border-ink/10">
                    <div className="overline flex items-center gap-2 text-amber-700">
                        <ShieldAlert className="w-3.5 h-3.5" /> Restore from backup
                    </div>
                    <h3 className="mt-1 font-display font-bold text-xl">Upload a previously-generated backup</h3>
                    <p className="text-xs text-ink/60 mt-2 leading-relaxed">
                        This reads an <code>.xlsx</code> backup file and writes its contents back into the database. Use it to recover after an accidental delete, or to migrate data between environments.
                    </p>
                </div>

                {result ? (
                    <div className="p-6" data-testid="restore-result">
                        <div className="flex items-center gap-2 text-emerald-700 mb-4">
                            <CheckCircle2 className="w-5 h-5" />
                            <span className="font-medium">Restore complete</span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <div className="overline">Inserted</div>
                                <div className="mt-1 font-display font-bold text-2xl tabular-nums">{result.total_inserted}</div>
                            </div>
                            <div>
                                <div className="overline">Updated</div>
                                <div className="mt-1 font-display font-bold text-2xl tabular-nums">{result.total_updated}</div>
                            </div>
                        </div>
                        <div className="mt-6 max-h-64 overflow-y-auto">
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-white">
                                    <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-ink/50 border-b border-ink/10">
                                        <th className="py-2">Sheet</th>
                                        <th className="py-2 text-right">Inserted</th>
                                        <th className="py-2 text-right">Updated</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(result.sheets || {}).map(([name, s]) => (
                                        <tr key={name} className="border-b border-ink/5">
                                            <td className="py-2 text-ink/70">{name}</td>
                                            <td className="py-2 text-right tabular-nums text-emerald-700">{s.inserted}</td>
                                            <td className="py-2 text-right tabular-nums text-klein">{s.updated}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {result.skipped_sheets?.length > 0 && (
                            <div className="mt-4 text-xs text-ink/50">
                                Skipped: {result.skipped_sheets.map((s) => `${s.sheet} (${s.reason})`).join(", ")}
                            </div>
                        )}
                        {result.warnings?.length > 0 && (
                            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded">
                                <div className="font-medium mb-1">Warnings ({result.warnings.length}):</div>
                                <ul className="list-disc pl-4 space-y-0.5 max-h-24 overflow-y-auto">
                                    {result.warnings.slice(0, 20).map((w, i) => (<li key={`${w}-${i}`}>{w}</li>))}
                                </ul>
                            </div>
                        )}
                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={onClose}
                                className="px-5 py-2.5 bg-ink text-white text-sm hover:bg-ink/90"
                                data-testid="restore-close-btn"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="p-6 space-y-5">
                            {/* File picker */}
                            <div>
                                <label className="overline block mb-2">Backup file</label>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept=".xlsx"
                                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                                    data-testid="restore-file-input"
                                    className="block w-full text-sm text-ink/70 file:mr-4 file:py-2 file:px-4 file:border file:border-ink/15 file:text-sm file:bg-bone hover:file:bg-ink/5"
                                />
                                {file && (
                                    <p className="mt-2 text-xs text-ink/60">
                                        Selected: <strong className="text-ink">{file.name}</strong> · {formatBytes(file.size)}
                                    </p>
                                )}
                            </div>

                            {/* Mode */}
                            <div>
                                <label className="overline block mb-2">Restore mode</label>
                                <div className="space-y-2">
                                    <label className="flex items-start gap-3 p-3 border border-ink/15 hover:bg-bone cursor-pointer">
                                        <input
                                            type="radio"
                                            name="mode"
                                            value="upsert"
                                            checked={mode === "upsert"}
                                            onChange={() => setMode("upsert")}
                                            className="mt-0.5"
                                            data-testid="restore-mode-upsert"
                                        />
                                        <div>
                                            <div className="text-sm font-medium text-ink">Upsert <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">Safe</span></div>
                                            <div className="text-xs text-ink/60 mt-0.5">Adds missing records and updates existing ones by <code>id</code>. Nothing is deleted. Recommended.</div>
                                        </div>
                                    </label>
                                    <label className="flex items-start gap-3 p-3 border border-ink/15 hover:bg-bone cursor-pointer">
                                        <input
                                            type="radio"
                                            name="mode"
                                            value="replace"
                                            checked={mode === "replace"}
                                            onChange={() => setMode("replace")}
                                            className="mt-0.5"
                                            data-testid="restore-mode-replace"
                                        />
                                        <div>
                                            <div className="text-sm font-medium text-ink">Replace <span className="ml-2 text-[10px] uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5">Destructive</span></div>
                                            <div className="text-xs text-ink/60 mt-0.5">Wipes each collection and replaces with the backup's contents. Your own admin user is kept so you don't get locked out.</div>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Users option */}
                            <div className="border border-ink/15 p-3 bg-bone/50">
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={includeUsers}
                                        onChange={(e) => setIncludeUsers(e.target.checked)}
                                        className="mt-0.5"
                                        data-testid="restore-include-users"
                                    />
                                    <div>
                                        <div className="text-sm font-medium text-ink">Include Users sheet</div>
                                        <div className="text-xs text-ink/60 mt-0.5">
                                            Backups do not contain password hashes. When enabled, existing users keep their current password and new users from the backup are skipped with a warning.
                                        </div>
                                    </div>
                                </label>
                            </div>

                            {mode === "replace" && (
                                <div className="p-3 bg-red-50 border border-red-200 text-red-800 text-xs rounded flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <div>
                                        <strong>Replace mode is destructive.</strong> Every collection listed in the backup will be wiped first. Any data added AFTER this backup was taken will be lost. You'll be asked to type <code>RESTORE</code> to confirm.
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="px-6 py-4 border-t border-ink/10 flex items-center justify-end gap-3">
                            <button
                                onClick={onClose}
                                disabled={running}
                                className="px-5 py-2.5 border border-ink/20 text-sm hover:bg-ink/5 disabled:opacity-60"
                                data-testid="restore-cancel-btn"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onSubmit}
                                disabled={running || !file}
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60"
                                data-testid="restore-submit-btn"
                            >
                                <Upload className="w-4 h-4" />
                                {running ? "Restoring…" : mode === "replace" ? "Replace database" : "Run restore"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div className={`bg-white p-6 ${accent ? "" : ""}`}>
            <div className="overline">{label}</div>
            <div className={`mt-3 font-display font-black tracking-tighter text-3xl ${accent ? "text-klein" : "text-ink"} tabular-nums truncate`} title={String(value)}>
                {value}
            </div>
        </div>
    );
}

function BackupRow({ item, isOpen, onToggle }) {
    const sheetEntries = Object.entries(item.sheets || {});
    return (
        <>
            <tr
                className="border-b border-ink/5 hover:bg-bone cursor-pointer"
                onClick={onToggle}
                data-testid={`backup-row-${item.id}`}
            >
                <td className="px-6 py-4">
                    <div className="text-ink">{formatTimestamp(item.created_at)}</div>
                    <div className="text-xs text-ink/50">{relativeTime(item.created_at)}</div>
                </td>
                <td className="px-4 py-4">
                    {item.delivered ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 border border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px] uppercase tracking-[0.15em]">
                            <CheckCircle2 className="w-3 h-3" /> Delivered
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 border border-red-200 bg-red-50 text-red-700 text-[10px] uppercase tracking-[0.15em]">
                            <AlertCircle className="w-3 h-3" /> Failed
                        </span>
                    )}
                    {!item.delivered && item.error && (
                        <div className="mt-1 text-xs text-red-700/80 max-w-[220px] truncate" title={item.error} data-testid={`backup-error-${item.id}`}>
                            {item.error.replace(/_/g, " ")}
                        </div>
                    )}
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-ink">{item.rows.toLocaleString()}</td>
                <td className="px-4 py-4 text-right tabular-nums text-ink">{formatBytes(item.size_bytes)}</td>
                <td className="px-4 py-4 text-ink/70 max-w-[260px] truncate" title={item.to}>{item.to || "—"}</td>
                <td className="px-4 py-4 text-ink/50 text-xs font-mono truncate max-w-[180px]" title={item.resend_id}>
                    {item.resend_id ? item.resend_id.slice(0, 12) + "…" : "—"}
                </td>
                <td className="px-4 py-4 text-xs text-klein">{isOpen ? "Hide" : "Details"}</td>
            </tr>
            {isOpen && (
                <tr className="bg-bone/40 border-b border-ink/5" data-testid={`backup-detail-${item.id}`}>
                    <td colSpan={7} className="px-6 py-6">
                        <div className="overline mb-3">Sheet breakdown</div>
                        {sheetEntries.length === 0 ? (
                            <div className="text-sm text-ink/50">No per-sheet metadata recorded for this backup.</div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-2 text-sm">
                                {sheetEntries.map(([name, count]) => (
                                    <div key={name} className="flex items-center justify-between border-b border-ink/5 py-1">
                                        <span className="text-ink/70">{name}</span>
                                        <span className="tabular-nums font-medium text-ink">{Number(count).toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="mt-4 text-xs text-ink/50 font-mono">
                            Resend ID: {item.resend_id || "—"}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}
